// 계약 탭 — 서식·조항·특약·지급회차·약정 수치·수급인 정보를 한 번에 저장한다(규칙이 서로 물려 있다).

import { ArrowDown, ArrowUp, ChevronDown, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Link } from "@/lib/nav";
import { ReactNode, useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import InlineField from "@/components/field";
import Pill from "@buildpy/ui/pill";
import SaveBar from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import InfoTip from "@buildpy/ui/info-tip";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { Textarea } from "@buildpy/ui/textarea";
import { formatNumber } from "@buildpy/core/lib/format";
import { nameInputMax, PERSON_NAME_MAX } from "@buildpy/core/modules/onboarding/rules";
import { settleRedirect } from "@/lib/redirect-error";
import { cn } from "@/lib/utils";
import {
  clauseAttribution,
  clauseBadge,
  defaultClauses,
  FTC_ART9_CITATION_HINT,
  hasFtcArt9Citation,
  judgeClauseSource,
} from "@buildpy/core/modules/contract/clauses";
import { LICENSE_INFO_LABEL, LICENSE_REQUIRED_HINT, LICENSE_REQUIRED_SOURCE } from "@buildpy/core/modules/contract/license-hint";
import {
  CLAUSE_BASES,
  ClauseBase,
  ContractSettingsForm,
  RATE_FIELDS,
  RATE_UNIT_SUFFIX,
  RateKey,
  SettingsIssue,
  bpToRate,
  clauseHeading,
  rateHintOf,
  rateToBp,
  savedContractCeo,
  showsDelayPenalty,
  suggestSpecialTerms,
  validateContractSettings,
  warrantyHintOf,
} from "@buildpy/core/modules/contract/settings";
import { ContractClause, SpecialTerm } from "@buildpy/core/modules/contract/types";
import { saveContractSettings } from "@/api/actions/org";

export interface ContractPanel {
  value: ContractSettingsForm;
  // 계약서 수급인 칸 중 회사 탭에서 고치는 것 — 여기서는 보여 주기만 한다.
  company: { name: string; address: string; tel: string };
}

interface Props extends ContractPanel {
  canEdit: boolean;
}

interface PaymentDraft {
  label: string;
  ratio: string;
  trigger_text: string;
}

interface WarrantyDraft {
  label: string;
  months: string;
}

const BASE_LABEL: Record<ClauseBase, string> = {
  FTC_10079: "공정위 표준 (실내건축·창호공사)",
  MOLIT: "국토부 민간건설공사 표준도급계약",
};

const move = <T,>(rows: T[], index: number, delta: number): T[] => {
  const next = index + delta;
  if (next < 0 || next >= rows.length) return rows;
  const copy = rows.slice();
  [copy[index], copy[next]] = [copy[next], copy[index]];
  return copy;
};

const rateText = (form: ContractSettingsForm): Record<RateKey, string> =>
  Object.fromEntries(
    RATE_FIELDS.map((f) => [f.key, String(bpToRate(form[f.key], f.unit))])
  ) as Record<RateKey, string>;

const monthsOf = (raw: string): number | null => {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  return Number(text);
};

const SettingsContract = ({ value, company, canEdit }: Props) => {
  const [pending, startTransition] = useTransition();
  const [dirty, setDirty] = useState(false);
  const [serverIssues, setServerIssues] = useState<SettingsIssue[]>([]);

  const [base, setBase] = useState<ClauseBase>(value.clause_base);
  const [clauses, setClauses] = useState<ContractClause[]>(value.clauses);
  const [terms, setTerms] = useState<SpecialTerm[]>(value.special_terms);
  const [payments, setPayments] = useState<PaymentDraft[]>(() =>
    value.payments.map((p) => ({
      label: p.label,
      ratio: String(bpToRate(p.ratio_bp, "percent")),
      trigger_text: p.trigger_text,
    }))
  );
  const [rates, setRates] = useState<Record<RateKey, string>>(() => rateText(value));
  const [warrantyMonths, setWarrantyMonths] = useState(String(value.warranty_months));
  const [warrantyTerms, setWarrantyTerms] = useState<WarrantyDraft[]>(() =>
    value.warranty_terms.map((t) => ({ label: t.label, months: String(t.months) }))
  );
  const [ceo, setCeo] = useState(value.company_ceo);
  const [bizNo, setBizNo] = useState(value.company_biz_no);
  const [license, setLicense] = useState(value.company_const_license_no);

  const touch = () => {
    setDirty(true);
    setServerIssues([]);
  };

  const source = judgeClauseSource(base, clauses);
  const badge = clauseBadge(source);
  const attribution = clauseAttribution(source);
  const art9Hint = hasFtcArt9Citation(clauses);
  const template = useMemo(() => defaultClauses(base), [base]);

  // 화면 글자 → 저장 한 벌. 숫자로 못 읽은 칸은 그 칸 이름으로 이슈를 남긴다.
  const { form, issues } = useMemo(() => {
    const parseIssues: SettingsIssue[] = [];
    const paymentRows = payments.map((p, i) => {
      const parsed = rateToBp(p.ratio, "percent");
      if (!parsed.ok) {
        parseIssues.push({
          field: `payments.${i}.ratio`,
          message: `${i + 1}회차 비율 — ${parsed.message}`,
        });
      }
      return {
        seq: i + 1,
        label: p.label,
        ratio_bp: parsed.ok ? parsed.bp : 0,
        trigger_text: p.trigger_text,
      };
    });
    const rateBp = {} as Record<RateKey, number>;
    for (const field of RATE_FIELDS) {
      const parsed = rateToBp(rates[field.key], field.unit);
      if (!parsed.ok) {
        parseIssues.push({
          field: `rates.${field.key}`,
          message: `${field.label} — ${parsed.message}`,
        });
      }
      rateBp[field.key] = parsed.ok ? parsed.bp : value[field.key];
    }
    const months = monthsOf(warrantyMonths);
    if (months === null) {
      parseIssues.push({ field: "warranty.months", message: "하자담보 기간은 개월 수(정수)입니다" });
    }
    const warranty = warrantyTerms.map((t, i) => {
      const m = monthsOf(t.months);
      if (m === null) {
        parseIssues.push({
          field: "warranty.terms",
          message: `예외 ${i + 1}번째 줄의 기간은 개월 수(정수)입니다`,
        });
      }
      return { label: t.label, months: m ?? 0 };
    });
    const next: ContractSettingsForm = {
      clause_base: base,
      clauses,
      special_terms: terms,
      payments: paymentRows,
      ...rateBp,
      warranty_months: months ?? 0,
      warranty_terms: warranty,
      company_ceo: ceo,
      company_biz_no: bizNo,
      company_const_license_no: license,
    };
    return {
      form: next,
      issues: parseIssues.length > 0 ? parseIssues : validateContractSettings(next),
    };
  }, [base, clauses, terms, payments, rates, warrantyMonths, warrantyTerms, ceo, bizNo, license, value]);

  const shownIssues = serverIssues.length > 0 ? serverIssues : issues;
  const issuesIn = (section: string) =>
    shownIssues.filter((issue) => issue.field.split(".")[0] === section);
  const fieldHas = (field: string) => shownIssues.some((issue) => issue.field === field);

  const ratioSum = form.payments.reduce((sum, p) => sum + p.ratio_bp, 0);
  const delayShown = showsDelayPenalty({ clause_base: base, clauses });
  const suggestions = suggestSpecialTerms(base, {
    special_terms: terms,
    delay_cap_bp: form.delay_cap_bp,
  });

  // 고친 조항이 있을 때 바꾸려는 서식 — 확인 창이 열려 있는 동안만 값이 있다.
  const [askBase, setAskBase] = useState<ClauseBase | null>(null);

  const applyBase = (next: ClauseBase) => {
    setBase(next);
    setClauses(defaultClauses(next));
    touch();
  };

  const switchBase = (next: ClauseBase) => {
    // 고친 조항은 서식을 바꾸면 사라지므로 한 번 묻는다.
    if (next === base) return;
    if (source === "CUSTOM") {
      setAskBase(next);
      return;
    }
    applyBase(next);
  };

  const revertClauses = () => {
    setClauses(defaultClauses(base));
    touch();
  };

  const patchClause = (index: number, patch: Partial<ContractClause>) => {
    setClauses((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    touch();
  };

  const addTerms = (added: { title: string; body: string }[]) => {
    setTerms((prev) => [
      ...prev,
      ...added.map((t, i) => ({ no: prev.length + i + 1, title: t.title, body: t.body })),
    ]);
    touch();
  };

  const onSave = () => {
    startTransition(async () => {
      try {
        const result = await saveContractSettings(form);
        setServerIssues(result.issues);
        if (result.ok) {
          setDirty(false);
          // 저장한 대표자는 정리값이다 — 새로고침 전에도 칸·요약이 같은 값을 보이게 맞춘다.
          setCeo(savedContractCeo(form.company_ceo));
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <div className="flex flex-col gap-y-6">
      {!canEdit && (
        <p className="text-sm text-muted-foreground">대표만 설정을 바꿀 수 있습니다.</p>
      )}

      <fieldset className="flex flex-col gap-y-3" disabled={!canEdit || pending}>
        <Section
          hint="새 계약에 들어가는 조항입니다. 원문과 한 글자라도 다르면 표준 표지가 빠집니다."
          issues={issuesIn("clauses")}
          notice={art9Hint ? FTC_ART9_CITATION_HINT : undefined}
          summary={`${BASE_LABEL[base]} · 조항 ${clauses.length}개${source === "CUSTOM" ? " · 고친 조항 있음" : ""}`}
          title="계약 서식과 조항">
          <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="계약 서식">
            {CLAUSE_BASES.map((b) => (
              <button
                key={b}
                aria-checked={b === base}
                className={cn(
                  "h-9 rounded-full border px-3.5 text-sm disabled:opacity-60",
                  b === base
                    ? "border-foreground bg-foreground text-background"
                    : "border-input bg-background"
                )}
                onClick={() => switchBase(b)}
                role="radio"
                type="button">
                {BASE_LABEL[b]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="clause-source">
            {badge ? (
              <Pill tone="primary">{badge}</Pill>
            ) : (
              <Pill>회사 자체 조항 — 원문과 달라 표준 표지가 빠졌습니다</Pill>
            )}
            {attribution && <span className="text-xs text-muted-foreground">{attribution}</span>}
            <Button
              disabled={source !== "CUSTOM"}
              onClick={revertClauses}
              size="sm"
              type="button"
              variant="outline">
              <RotateCcw className="mr-1 size-3.5" />
              원문으로 되돌리기
            </Button>
          </div>
          <div className="flex flex-col divide-y rounded-md border">
            {clauses.map((clause, i) => {
              const origin = template[i];
              const edited =
                !origin || origin.title !== clause.title || origin.body !== clause.body;
              return (
                <details key={`${base}-${clause.no}`} className="px-3 py-2">
                  <summary className="flex cursor-pointer items-center gap-x-2 text-sm">
                    <span className="font-medium">{clauseHeading(clause)}</span>
                    <span>{clause.title}</span>
                    {edited && (
                      <span className="rounded-md bg-warning-bg px-1.5 text-xs text-warning">고침</span>
                    )}
                  </summary>
                  <div className="mt-2 flex flex-col gap-y-2">
                    <Input
                      aria-label={`${clauseHeading(clause)} 제목`}
                      inputSize="sm"
                      name={`clause-title-${i}`}
                      onChange={(e) => patchClause(i, { title: e.target.value })}
                      value={clause.title}
                    />
                    <Textarea
                      aria-label={`${clauseHeading(clause)} 본문`}
                      className="min-h-40 text-sm"
                      onChange={(e) => patchClause(i, { body: e.target.value })}
                      value={clause.body}
                    />
                  </div>
                </details>
              );
            })}
          </div>
        </Section>

        <Section
          hint="원문이 다루지 않는 약속은 조항을 고치지 말고 특약으로 적습니다. 조항과 따로 저장됩니다."
          issues={issuesIn("special_terms")}
          summary={terms.length > 0 ? `특약 ${terms.length}건` : "특약 없음"}
          title="특약">
          {terms.length === 0 && (
            <p className="text-sm text-muted-foreground">아직 특약이 없습니다.</p>
          )}
          {terms.map((term, i) => (
            <div key={i} className="flex flex-col gap-y-2 rounded-md border p-3">
              <div className="flex items-center gap-x-2">
                <span className="w-8 shrink-0 text-sm text-muted-foreground">{i + 1}.</span>
                <Input
                  aria-label={`특약 ${i + 1} 제목`}
                  inputSize="sm"
                  name={`term-title-${i}`}
                  onChange={(e) => {
                    setTerms((prev) => prev.map((t, j) => (j === i ? { ...t, title: e.target.value } : t)));
                    touch();
                  }}
                  placeholder="제목"
                  value={term.title}
                />
                <RowButtons
                  label={`특약 ${i + 1}`}
                  onDown={() => {
                    setTerms((prev) => move(prev, i, 1));
                    touch();
                  }}
                  onRemove={() => {
                    setTerms((prev) => prev.filter((_, j) => j !== i));
                    touch();
                  }}
                  onUp={() => {
                    setTerms((prev) => move(prev, i, -1));
                    touch();
                  }}
                />
              </div>
              <Textarea
                aria-label={`특약 ${i + 1} 내용`}
                className="min-h-20 text-sm"
                onChange={(e) => {
                  setTerms((prev) => prev.map((t, j) => (j === i ? { ...t, body: e.target.value } : t)));
                  touch();
                }}
                placeholder="내용"
                value={term.body}
              />
            </div>
          ))}
          <div>
            <Button
              onClick={() => addTerms([{ title: "", body: "" }])}
              size="sm"
              type="button"
              variant="outline">
              <Plus className="mr-1 size-3.5" />
              특약 추가
            </Button>
          </div>
          {suggestions.length > 0 && (
            <div className="flex flex-col gap-y-2 rounded-md bg-muted/50 p-3">
              <div className="flex items-center justify-between gap-x-2">
                <span className="text-sm font-medium">
                  이 서식 원문에 없는 항목 {suggestions.length}개
                </span>
                <Button onClick={() => addTerms(suggestions)} size="sm" type="button">
                  한 번에 넣기
                </Button>
              </div>
              {suggestions.map((s) => (
                <div key={s.covers} className="flex items-start justify-between gap-x-3 text-sm">
                  <div>
                    <span className="font-medium">{s.title}</span>
                    <p className="text-xs text-muted-foreground">{s.body}</p>
                  </div>
                  <Button onClick={() => addTerms([s])} size="sm" type="button" variant="outline">
                    넣기
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          hint="새 계약의 지급회차 기본값입니다. 비율의 합이 100% 여야 하고, 지급 시기는 꼭 적습니다."
          issues={issuesIn("payments")}
          summary={`${payments.length}회차 · 합계 ${formatNumber(bpToRate(ratioSum, "percent"))}%`}
          title="기본 지급회차">
          <div className={cn("hidden items-center gap-2 text-xs text-muted-foreground lg:grid", PAY_TRACKS)}>
            <span>회차 이름</span>
            <span>비율 (%)</span>
            <span>지급 시기</span>
            <span />
          </div>
          {payments.map((row, i) => (
            <div key={i} className={cn(PAY_ROW, PAY_TRACKS)}>
              <InlineField className={PAY_CELL} label="회차 이름">
                <Input
                  aria-invalid={fieldHas(`payments.${i}.label`)}
                  aria-label={`${i + 1}회차 이름`}
                  className="min-w-0"
                  inputSize="sm"
                  name={`payment-label-${i}`}
                  onChange={(e) => {
                    setPayments((prev) => prev.map((p, j) => (j === i ? { ...p, label: e.target.value } : p)));
                    touch();
                  }}
                  value={row.label}
                />
              </InlineField>
              <InlineField className={PAY_CELL} label="비율 (%)">
                <Input
                  aria-invalid={fieldHas(`payments.${i}.ratio`)}
                  aria-label={`${i + 1}회차 비율`}
                  inputMode="decimal"
                  inputSize="sm"
                  name={`payment-ratio-${i}`}
                  onChange={(e) => {
                    setPayments((prev) => prev.map((p, j) => (j === i ? { ...p, ratio: e.target.value } : p)));
                    touch();
                  }}
                  value={row.ratio}
                />
              </InlineField>
              <InlineField className={cn(PAY_CELL, "col-span-2 sm:col-span-1")} label="지급 시기">
                <Input
                  aria-invalid={fieldHas(`payments.${i}.trigger_text`)}
                  aria-label={`${i + 1}회차 지급 시기`}
                  inputSize="sm"
                  name={`payment-trigger-${i}`}
                  onChange={(e) => {
                    setPayments((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, trigger_text: e.target.value } : p))
                    );
                    touch();
                  }}
                  placeholder="예: 도기세팅 또는 타일 완료"
                  value={row.trigger_text}
                />
              </InlineField>
              <RowButtons
                className="col-start-2 lg:col-start-auto"
                label={`${i + 1}회차`}
                onDown={() => {
                  setPayments((prev) => move(prev, i, 1));
                  touch();
                }}
                onRemove={() => {
                  setPayments((prev) => prev.filter((_, j) => j !== i));
                  touch();
                }}
                onUp={() => {
                  setPayments((prev) => move(prev, i, -1));
                  touch();
                }}
              />
            </div>
          ))}
          <div className="flex items-center gap-x-3">
            <Button
              onClick={() => {
                setPayments((prev) => [...prev, { label: "", ratio: "0", trigger_text: "" }]);
                touch();
              }}
              size="sm"
              type="button"
              variant="outline">
              <Plus className="mr-1 size-3.5" />
              회차 추가
            </Button>
            <span
              className={cn("text-sm", fieldHas("payments.sum") ? "text-destructive" : "text-muted-foreground")}
              data-testid="payment-sum">
              합계 {formatNumber(bpToRate(ratioSum, "percent"))}%
            </span>
          </div>
        </Section>

        <Section
          hint="계약서에 들어가는 비율입니다. 계약마다 따로 고칠 수 있습니다."
          issues={issuesIn("rates")}
          summary={RATE_FIELDS.filter((f) => delayShown || !f.delayOnly)
            .map((f) => `${f.label} ${rates[f.key]}${RATE_UNIT_SUFFIX[f.unit]}`)
            .join(" · ")}
          title="약정 수치">
          {!delayShown && (
            <p className="rounded-md bg-muted/50 p-3 text-sm" data-testid="ftc-delay-notice">
              공정위 서식에는 지체상금 조항이 없습니다 — 대신 지연이자율(제6조)이 쓰입니다.
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {RATE_FIELDS.filter((f) => delayShown || !f.delayOnly).map((f) => (
              <Field key={f.key} hint={rateHintOf(f, source)} label={`${f.label} (${RATE_UNIT_SUFFIX[f.unit]})`} name={`rate-${f.key}`}>
                <Input
                  aria-invalid={fieldHas(`rates.${f.key}`)}
                  id={`rate-${f.key}`}
                  inputMode="decimal"
                  inputSize="sm"
                  name={`rate-${f.key}`}
                  onChange={(e) => {
                    setRates((prev) => ({ ...prev, [f.key]: e.target.value }));
                    touch();
                  }}
                  value={rates[f.key]}
                />
              </Field>
            ))}
          </div>
        </Section>

        <Section
          hint={warrantyHintOf(source)}
          issues={issuesIn("warranty")}
          summary={`일반 ${warrantyMonths}개월${warrantyTerms.length > 0 ? ` · 예외 ${warrantyTerms.length}건` : ""}`}
          title="하자담보">
          <Field label="일반 기간 (개월)" name="warranty-months">
            <div className="max-w-32">
              <Input
                aria-invalid={fieldHas("warranty.months")}
                id="warranty-months"
                inputMode="numeric"
                inputSize="sm"
                name="warranty-months"
                onChange={(e) => {
                  setWarrantyMonths(e.target.value);
                  touch();
                }}
                value={warrantyMonths}
              />
            </div>
          </Field>
          {warrantyTerms.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_6rem_auto] items-center gap-2">
              <Input
                aria-label={`예외 ${i + 1} 공종`}
                inputSize="sm"
                name={`warranty-label-${i}`}
                onChange={(e) => {
                  setWarrantyTerms((prev) => prev.map((t, j) => (j === i ? { ...t, label: e.target.value } : t)));
                  touch();
                }}
                placeholder="공종 (예: 창호)"
                value={row.label}
              />
              <Input
                aria-label={`예외 ${i + 1} 기간`}
                inputMode="numeric"
                inputSize="sm"
                name={`warranty-months-${i}`}
                onChange={(e) => {
                  setWarrantyTerms((prev) => prev.map((t, j) => (j === i ? { ...t, months: e.target.value } : t)));
                  touch();
                }}
                value={row.months}
              />
              <Button
                aria-label={`예외 ${i + 1} 삭제`}
                onClick={() => {
                  setWarrantyTerms((prev) => prev.filter((_, j) => j !== i));
                  touch();
                }}
                size="sm"
                type="button"
                variant="ghost">
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <div>
            <Button
              onClick={() => {
                setWarrantyTerms((prev) => [...prev, { label: "", months: "12" }]);
                touch();
              }}
              size="sm"
              type="button"
              variant="outline">
              <Plus className="mr-1 size-3.5" />
              예외 추가
            </Button>
          </div>
        </Section>

        <Section
          hint="계약서의 수급인 칸입니다. 상호·주소·전화는 회사 탭에서 고칩니다."
          issues={issuesIn("company")}
          summary={`대표자 ${ceo || "미입력"} · 사업자등록번호 ${bizNo || "미입력"}`}
          title="수급인 정보">
          <dl className="grid gap-2 text-sm md:grid-cols-3">
            <ReadOnly label="상호" value={company.name} />
            <ReadOnly label="주소" value={company.address} />
            <ReadOnly label="전화" value={company.tel} />
          </dl>
          <Link className="text-sm underline" href="/app/settings">
            회사 탭에서 고치기
          </Link>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="대표자" name="company-ceo">
              <Input
                aria-invalid={fieldHas("company.company_ceo")}
                id="company-ceo"
                inputSize="sm"
                maxLength={nameInputMax(PERSON_NAME_MAX)}
                name="company-ceo"
                onChange={(e) => {
                  setCeo(e.target.value);
                  touch();
                }}
                value={ceo}
              />
            </Field>
            <Field label="사업자등록번호" name="company-biz-no">
              <Input
                aria-invalid={fieldHas("company.company_biz_no")}
                id="company-biz-no"
                inputSize="sm"
                name="company-biz-no"
                onChange={(e) => {
                  setBizNo(e.target.value);
                  touch();
                }}
                placeholder="000-00-00000"
                value={bizNo}
              />
            </Field>
            <Field
              info={
                <InfoTip label={LICENSE_INFO_LABEL}>
                  {LICENSE_REQUIRED_HINT}
                  <span className="mt-1 block text-muted-foreground">{LICENSE_REQUIRED_SOURCE}</span>
                </InfoTip>
              }
              label="건설업 등록번호"
              name="company-license">
              <Input
                id="company-license"
                inputSize="sm"
                name="company-license"
                onChange={(e) => {
                  setLicense(e.target.value);
                  touch();
                }}
                value={license}
              />
            </Field>
          </div>
        </Section>
      </fieldset>

      {canEdit && (
        <SaveBar
          blocked={
            issues.length > 0
              ? `저장할 수 없습니다 — ${issues[0].message}${issues.length > 1 ? ` 외 ${issues.length - 1}건` : ""}`
              : undefined
          }
          dirty={dirty}
          onSave={onSave}
          pending={pending}
        />
      )}
      <ConfirmDialog
        confirmLabel="서식 바꾸기"
        description="고친 조항이 모두 사라지고 새 서식의 원문으로 바뀝니다."
        open={askBase !== null}
        testId="contract-base-confirm"
        title="계약 서식을 바꿀까요?"
        onConfirm={() => askBase && applyBase(askBase)}
        onOpenChange={(open) => !open && setAskBase(null)}
      />
    </div>
  );
};

export default SettingsContract;

// 접히는 섹션 — 접힌 채로도 요약 한 줄로 값을 보고, 막힌 칸이 새로 생기면 저절로 펼친다(닫는 건 사람만).
const Section = ({
  title,
  hint,
  summary,
  notice,
  issues,
  children,
}: {
  title: string;
  hint: string;
  summary: string;
  // 접힌 채로도 꼭 보여야 하는 안내 — 요약 줄 아래에 둔다.
  notice?: ReactNode;
  issues: SettingsIssue[];
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(issues.length > 0);
  const [seenIssues, setSeenIssues] = useState(issues.length);
  if (issues.length !== seenIssues) {
    setSeenIssues(issues.length);
    if (issues.length > seenIssues) setOpen(true);
  }
  return (
    <SectionCard
      as="details"
      data-testid="contract-settings-section"
      open={open}
      padding="none"
      onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-4 py-3">
        <ChevronDown
          aria-hidden
          className={cn("row-start-1 size-4 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")}
        />
        <h2 className="col-start-2 row-start-1 text-base font-semibold">{title}</h2>
        {issues.length > 0 && (
          <Pill className="col-start-3 row-start-1" tone="danger">
            확인 {issues.length}
          </Pill>
        )}
        <span className="col-start-2 truncate text-xs text-muted-foreground" data-testid="contract-settings-summary">
          {summary}
        </span>
        {notice && (
          <span className="col-start-2 text-xs text-muted-foreground" data-testid="contract-settings-notice">
            {notice}
          </span>
        )}
      </summary>
      <div className="flex flex-col gap-y-3 border-t border-border p-4">
        <p className="text-xs text-muted-foreground">{hint}</p>
        {issues.length > 0 && (
          <ul className="flex flex-col gap-y-0.5 text-sm text-destructive" role="alert">
            {issues.map((issue, i) => (
              <li key={`${issue.field}-${i}`}>{issue.message}</li>
            ))}
          </ul>
        )}
        {children}
      </div>
    </SectionCard>
  );
};

const Field = ({
  label,
  name,
  hint,
  info,
  children,
}: {
  label: string;
  name: string;
  hint?: string;
  info?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-y-1.5">
    {info ? (
      <div className="flex flex-wrap items-center gap-x-1">
        <Label htmlFor={name}>{label}</Label>
        {info}
      </div>
    ) : (
      <Label htmlFor={name}>{label}</Label>
    )}
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const ReadOnly = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd>{value || "—"}</dd>
  </div>
);

// 기본 지급회차 — lg 부터 머리줄과 같은 트랙(끝 7.5rem = RowButtons 의 sm 버튼 3개), 그 아래는 이름·비율 / 지급 시기·버튼 카드(sm 미만은 버튼만 셋째 줄) — 시각 순서 = DOM(탭) 순서라 dense 를 쓰지 않는다.
const PAY_TRACKS = "lg:grid-cols-[6rem_6rem_minmax(0,1fr)_7.5rem]";
const PAY_ROW =
  "grid grid-cols-[minmax(0,1fr)_7.5rem] items-end gap-2 rounded-md border p-3 lg:items-center lg:border-0 lg:p-0";
const PAY_CELL = "min-w-0 lg:[&>span:first-child]:hidden";

const RowButtons = ({
  className,
  label,
  onUp,
  onDown,
  onRemove,
}: {
  className?: string;
  label: string;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) => (
  <div className={cn("flex shrink-0 items-center", className)}>
    <Button aria-label={`${label} 위로`} onClick={onUp} size="sm" type="button" variant="ghost">
      <ArrowUp className="size-4" />
    </Button>
    <Button aria-label={`${label} 아래로`} onClick={onDown} size="sm" type="button" variant="ghost">
      <ArrowDown className="size-4" />
    </Button>
    <Button aria-label={`${label} 삭제`} onClick={onRemove} size="sm" type="button" variant="ghost">
      <Trash2 className="size-4" />
    </Button>
  </div>
);
